import React, {useState, useEffect} from 'react';
import {Box, VStack, HStack, IconButton, useTheme, Progress, Text, Image, useToast, Modal, ModalOverlay, ModalContent, ModalBody} from '@chakra-ui/react';
import {motion, AnimatePresence} from 'framer-motion';
import IntroWelcome from './IntroWelcome';
import IntroFeatures from './IntroFeatures';
import IntroCallToAction from './IntroCallToAction';
import IntroPhone from './IntroPhone';
import IntroVerify from './IntroVerify';
import IntroAccountInfo from './IntroAccountInfo';
import IntroAddress from './IntroAddress';
import IntroBookmarks from './IntroBookmarks';
import IntroPlaces from '../BookmarkPlaces/IntroPlaces';
import IntroUsername from './IntroUsername';
import IntroAvatar from './IntroAvatar';
import IntroNotifications from './IntroNotifications';
import Icon from '../../components/Icon';
import {useNavigate, useSearchParams} from 'react-router-dom';
import angleLeft from '../../assets/images/angle-left.png';
import wordLogo from '../../assets/logos/Word_logo3.png';
import {TextG14, TextG16, TextG24} from '../../components/typography';
import {register, requestVerificationCode, validateSmsCode, fetchStateList, softCheck, updateUser} from '../../endpoints/api';
import {logAnalyticsEvent} from '../../utils/analytics';
import useAuth from '../../hooks/useAuth';
import {useAddUserAddress, useFetchAddressTypes, useGetUserAddresses, useUpdateUserAddress} from '../../hooks/useUserAddress';
import {AddressData} from './IntroAddress';
import {find} from 'lodash';
import SoftCheckModal from '../../components/SoftCheckModal';
import * as pushService from '../../pushService';
import {isIphone, deviceDetected} from '../../utils/deviceCheck';
import {useOnboardingProgress} from '../../hooks/useOnboardingProgress';
import {useLogTouchpoint} from '../../hooks/useTouchpointTracking';
import {useProfileFieldsStatus} from '../../hooks/useProfileFieldsStatus';
import logger from '../../utils/logger';
import {addDismissedBanner} from '../../state/GlobalBanner/GlobalBannerManager';

const TOTAL_STEPS = 12;

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const initialStep = stepParam ? Math.min(Math.max(parseInt(stepParam, 10) - 1, 0), TOTAL_STEPS - 1) : 0;
  const inviteCode = searchParams.get('code') || searchParams.get('invite_code');
  const ni = searchParams.get('ni');
  const qrCodeId = searchParams.get('qr_code_id');
  const fromBanner = searchParams.get('from') === 'banner';
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [previousStep, setPreviousStep] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    wantsEmails: false,
  });
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [address, setAddress] = useState<AddressData | null>(null);
  const [username, setUsername] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showSoftCheckModal, setShowSoftCheckModal] = useState(false);
  const [softCheckCompleted, setSoftCheckCompleted] = useState(false);
  const [pendingAddressSubmission, setPendingAddressSubmission] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [sessionBookmarkCount, setSessionBookmarkCount] = useState(0);
  const [showBookmarkExitModal, setShowBookmarkExitModal] = useState(false);
  const [sessionPlacesCount, setSessionPlacesCount] = useState(0);
  const [showPlacesExitModal, setShowPlacesExitModal] = useState(false);
  // Transitional overlay shown over the events Bookmarks step on entry from the
  // Places step. Suppresses IntroBookmarks's own welcome modal while active.
  const [showPlacesToEventsBridge, setShowPlacesToEventsBridge] = useState(false);
  
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const {login, user: authUser} = useAuth();
  const {initializeTracking, markStepComplete, markOnboardingComplete, forceInitialize, isInitialized, firstIncompleteStep} = useOnboardingProgress();
  const logTouchpointMutation = useLogTouchpoint();
  const addUserAddressMutation = useAddUserAddress();
  const updateUserAddressMutation = useUpdateUserAddress();
  const fetchAddressTypes = useFetchAddressTypes();
  const addressTypes = fetchAddressTypes.data?.data;
  const {userAddresses} = useGetUserAddresses({
    userId: authUser?.id,
    addressType: 'HOME',
  });
  const profileFields = useProfileFieldsStatus();

  // Force initialize onboarding tracking for existing users who land on onboarding
  useEffect(() => {
    if (authUser?.id && !isInitialized) {
      forceInitialize();
    }
  }, [authUser?.id, isInitialized, forceInitialize]);

  // Check authentication status and redirect to appropriate step
  useEffect(() => {
    if (hasCheckedAuth) return;
    
    // If user is authenticated (already created account)
    if (authUser?.id) {
      // Determine target step - use firstIncompleteStep from touchpoint tracking if coming from banner
      // Otherwise default to step 7 (index 6)
      const targetStep = fromBanner && firstIncompleteStep ? firstIncompleteStep : 7;
      const targetStepIndex = targetStep - 1; // Convert to 0-based index
      
      // User exists - redirect to at least the target step
      // Only redirect if they're on an earlier step
      if (currentStep < targetStepIndex) {
        setCurrentStep(targetStepIndex);
        const params = new URLSearchParams(searchParams);
        params.set('step', targetStep.toString());
        if (fromBanner) params.set('from', 'banner');
        if (inviteCode) params.set('code', inviteCode);
        if (ni) params.set('ni', ni);
        if (qrCodeId) params.set('qr_code_id', qrCodeId);
        navigate(`?${params.toString()}`, { replace: true });
      }
    } else {
      // User is not authenticated (not created yet)
      // If they're trying to access a step beyond the intro screens (steps 0-2), redirect to step 1
      if (currentStep > 2) {
        setCurrentStep(0);
        const params = new URLSearchParams(searchParams);
        params.set('step', '1'); // Step 1 in 1-based indexing
        if (inviteCode) params.set('code', inviteCode);
        if (ni) params.set('ni', ni);
        if (qrCodeId) params.set('qr_code_id', qrCodeId);
        navigate(`?${params.toString()}`, { replace: true });
      }
    }
    
    setHasCheckedAuth(true);
  }, [authUser, hasCheckedAuth, currentStep, searchParams, inviteCode, ni, qrCodeId, navigate, fromBanner, firstIncompleteStep]);

  // Load existing HOME address when user reaches address step
  useEffect(() => {
    if (currentStep === 6 && userAddresses && userAddresses.length > 0 && !address) {
      const homeAddress = userAddresses[0]; // Get first HOME address
      if (homeAddress) {
        // Format the existing address for display similar to Google Places format
        // Build address parts
        const addressParts: string[] = [];
        
        // Street address with unit if present
        if (homeAddress.street_address) {
          addressParts.push(homeAddress.unit ? `${homeAddress.street_address} ${homeAddress.unit}` : homeAddress.street_address);
        }
        
        // City, State ZIP format
        const cityStateZip = [
          homeAddress.city?.name || homeAddress.city,
          homeAddress.state?.abbreviation || '',
          homeAddress.zip_code,
        ]
          .filter(Boolean)
          .join(' ');
        
        if (cityStateZip) {
          addressParts.push(cityStateZip);
        }
        
        const formattedAddress = addressParts.join(', ');
      
        
        // Create a minimal AddressData object from existing address
        // Note: We don't have the full Google Places object, but we have the parsed data
        setAddress({
          formatted_address: formattedAddress,
          place: {
            formatted_address: formattedAddress,
            address_components: [
              {types: ['street_number', 'street_address'], long_name: homeAddress.street_address || ''},
              {types: ['locality'], long_name: homeAddress.city?.name || homeAddress.city || ''},
              {types: ['administrative_area_level_1'], short_name: homeAddress.state?.abbreviation || ''},
              {types: ['postal_code'], long_name: homeAddress.zip_code || ''},
            ],
          },
        });
      }
    }
  }, [currentStep, userAddresses, address]);

  const steps = [
    {component: IntroWelcome, buttonText: 'Continue', showBackButton: false, showProgress: false},
    {component: IntroFeatures, buttonText: 'Continue', showBackButton: true, showProgress: false},
    {component: IntroCallToAction, buttonText: "I'm in!", showBackButton: true, showProgress: false},
    {
      component: IntroPhone,
      buttonText: 'Send Code',
      showBackButton: true,
      showProgress: true,
      requiresValidation: true,
      progressStep: 1,
      progressText: 'Verifying your identity...',
    },
    {
      component: IntroVerify,
      buttonText: 'Verify',
      showBackButton: true,
      showProgress: true,
      requiresValidation: true,
      progressStep: 2,
      progressText: 'Almost there...',
    },
    {
      component: IntroAccountInfo,
      buttonText: 'Create Account',
      showBackButton: true,
      showProgress: true,
      requiresValidation: true,
      progressStep: 3,
      progressText: 'Starting your connection...',
    },
    {
      component: IntroAddress,
      buttonText: 'Save Address',
      showBackButton: true,
      showProgress: true,
      requiresValidation: true,
      progressStep: 4,
      progressText: 'Setting up your neighborhood...',
    },
    {
      component: IntroUsername,
      buttonText: 'Looks Good!',
      showBackButton: true,
      showProgress: true,
      requiresValidation: true,
      progressStep: 5,
      progressText: 'Creating your identity...',
    },
    {
      component: IntroAvatar,
      buttonText: 'Continue',
      showBackButton: true,
      showProgress: true,
      requiresValidation: false,
      progressStep: 6,
      progressText: 'Finishing touches...',
    },
    {
      component: IntroPlaces,
      buttonText: 'Continue',
      showBackButton: true,
      showProgress: true,
      requiresValidation: false,
      progressStep: 7,
      progressText: 'Picking your places...',
    },
    {
      component: IntroBookmarks,
      buttonText: 'Continue',
      showBackButton: true,
      showProgress: true,
      requiresValidation: false,
      progressStep: 8,
      progressText: 'Building your vibe...',
    },
    {
      component: IntroNotifications,
      buttonText: 'Done!',
      showBackButton: false,
      showProgress: false,
      requiresValidation: false,
      progressStep: 9,
      progressText: 'Setting up notifications...',
    },
  ];

  // Calculate progress based on steps with progress bars
  const progressSteps = steps.filter((step) => step.showProgress).length;
  const currentProgressStep = steps[currentStep].progressStep || 0;
  const progressPercentage = progressSteps > 0 ? (currentProgressStep / progressSteps) * 100 : 0;

  // Reset soft check when address changes
  useEffect(() => {
    setSoftCheckCompleted(false);
  }, [address]);

  // Update URL step parameter when currentStep changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('step', (currentStep + 1).toString());
    
    // Preserve other parameters
    if (inviteCode) params.set('code', inviteCode);
    if (ni) params.set('ni', ni);
    if (qrCodeId) params.set('qr_code_id', qrCodeId);
    
    navigate(`?${params.toString()}`, { replace: true });
  }, [currentStep]);

  /**
   * Get the next step that needs user input, skipping steps where field values already exist.
   * Only skips steps 6 (address), 7 (username), 8 (avatar) — earlier and later steps are never skipped.
   * When coming from a banner prompt, don't skip — let the user confirm their existing values.
   */
  const getNextIncompleteStep = (fromStep: number): number => {
    if (fromBanner) return fromStep + 1;
    let next = fromStep + 1;
    if (next === 6 && profileFields.home_address.has_value) next++;
    if (next === 7 && profileFields.handle.has_value) next++;
    if (next === 8 && profileFields.profile_photo.has_value) next++;
    return next;
  };

  const handleNext = async () => {
    if (isLoading) return;
    
    // Step 3 (Phone number) - Send verification code only
    if (currentStep === 3) {
      try {
        setIsLoading(true);
        
        await requestVerificationCode(phone);
        
        toast({
          title: 'Verification code sent',
          description: 'Check your phone for the 6-digit code',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setIsLoading(false);
        setDirection(1);
        setPreviousStep(currentStep);
        setCurrentStep(currentStep + 1);
      } catch (error: any) {
        setIsLoading(false);
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to send verification code',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Error sending verification code:', error);
      }
      return;
    }
    
    // Step 4 (Verification) - Validate SMS code only
    if (currentStep === 4) {
      try {
        setIsLoading(true);
        
        await validateSmsCode(phone, verificationCode);
        
        toast({
          title: 'Phone verified!',
          description: 'Your phone number has been confirmed',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setIsLoading(false);
        setDirection(1);
        setPreviousStep(currentStep);
        setCurrentStep(currentStep + 1);
      } catch (error: any) {
        setIsLoading(false);
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Invalid verification code',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Error verifying code:', error);
      }
      return;
    }
    
    // Step 5 (Form) - Register account and log in
    if (currentStep === 5) {
      try {
        setIsLoading(true);
        
        // Register the account
        const params = {
          name: formData.firstName,
          surname: formData.lastName,
          email: formData.email.toLowerCase(),
          phone: phone,
          password: formData.password,
          ...(inviteCode && {invite_code: inviteCode}),
          ...(ni && {ni}),
          ...(qrCodeId && {qr_code_id: qrCodeId}),
          tou_pp_accepted: true,
        };
        
        await register(params);

        // Log successful user registration to Google Analytics
        logAnalyticsEvent('sign_up', {method: 'email'});

        // Initialize onboarding progress tracking for new user
        initializeTracking();

        // Log in the user
        await login(formData.email.toLowerCase(), formData.password);
        
        // Note: userData will be available from authUser after login completes and useEffect runs
        // For address submission, we'll use authUser directly
        
        toast({
          title: 'Account created!',
          description: 'Welcome to the community',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setIsLoading(false);
        setDirection(1);
        setPreviousStep(currentStep);
        setCurrentStep(currentStep + 1);
      } catch (error: any) {
        setIsLoading(false);
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to create account',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Error creating account:', error);
      }
      return;
    }
    
    // Step 6 (Address) - Submit address
    if (currentStep === 6) {
      try {
        setIsLoading(true);
        
        if (!address) {
          throw new Error('Please select an address from the dropdown');
        }
        
        if (!authUser?.id) {
          throw new Error('User data is missing. Please go back and complete registration.');
        }
        
        // Parse address components from Google Places
        const place = address.place;
        const addressComponents = place.address_components || [];
        
        // Extract address parts
        let streetNumber = '';
        let route = '';
        let unit = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        for (const component of addressComponents) {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('subpremise')) {
            unit = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.short_name; // Use abbreviation
          } else if (types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        }
        
        const streetAddress = `${streetNumber} ${route}`.trim();
        
        if (!streetAddress || !city || !state || !zipCode) {
          throw new Error('Incomplete address information. Please select a complete address from the dropdown.');
        }
        
        // Perform soft check if not already completed and not pending from modal
        if (!softCheckCompleted && !pendingAddressSubmission) {
          try {
            await softCheck({
              name: formData.firstName || authUser?.name || '',
              surname: formData.lastName || authUser?.surname || '',
              zip_code: zipCode,
            });
            // If we get here, zip code is in coverage area
            setSoftCheckCompleted(true);
          } catch (error) {
            // Soft check failed (zip code not in coverage area)
            setIsLoading(false);
            setPendingAddressSubmission(true);
            setShowSoftCheckModal(true);
            return; // Stop the address submission
          }
        } else if (pendingAddressSubmission) {
          // User clicked continue from modal, skip soft check and mark as completed
          setSoftCheckCompleted(true);
          setPendingAddressSubmission(false);
        }
        
        // Fetch states to get state_id
        const statesResponse = await fetchStateList();
        const states = statesResponse.data;
        const stateData = states.find((s: any) => s.abbreviation === state);
        
        if (!stateData) {
          throw new Error(`State ${state} not found`);
        }
        
        // Get the HOME address type ID
        const homeTypeId = find(addressTypes, {name: 'HOME'})?.id;
        if (!homeTypeId) {
          throw new Error('HOME address type not found');
        }
        
        // Check if user already has a HOME address
        const existingHomeAddress = userAddresses?.find((addr: any) => addr.type_id === homeTypeId);
        
        const addressPayload = {
          type_id: homeTypeId,
          street_address: streetAddress,
          unit: unit || '', // Send empty string to clear unit if not present
          city: city,
          state_id: stateData.id,
          zip_code: zipCode,
        };
        
        // Submit address - update if exists, add if new
        if (existingHomeAddress) {
          await updateUserAddressMutation.mutateAsync({
            userId: authUser.id,
            addressId: existingHomeAddress.id,
            address: addressPayload,
          });
          // User had existing address and confirmed/updated it - log touchpoint
          // to prevent re-prompt loop (backend may not log if address unchanged)
          logTouchpointMutation.mutate({touchpoint: 'home_address_confirmation', action: 'complete'});
        } else {
          await addUserAddressMutation.mutateAsync({
            userId: authUser.id,
            address: addressPayload,
          });
        }
        
        toast({
          title: 'Address saved!',
          description: 'Your home address has been added',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Mark step 6 (IntroAddress) as complete
        markStepComplete(6);

        setIsLoading(false);
        setDirection(1);
        setPreviousStep(currentStep);
        setCurrentStep(getNextIncompleteStep(currentStep));
      } catch (error: any) {
        setIsLoading(false);
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || 'Failed to save address',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Error saving address:', error);
      }
      return;
    }
    
    // Step 7 (Username) - Update user handle
    if (currentStep === 7) {
      try {
        setIsLoading(true);
        
        if (!username || username.length <= 2) {
          throw new Error('Please enter a valid username');
        }
        
        await updateUser({handle: username});
        
        // Always log touchpoint as complete so user won't be re-prompted
        // (This is a one-time prompt - once confirmed, never show again)
        logTouchpointMutation.mutate({touchpoint: 'user_handle_update', action: 'complete'});
        
        toast({
          title: 'Username saved!',
          description: 'Your nickname is all set',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Mark step 7 (IntroUsername) as complete
        markStepComplete(7);

        setIsLoading(false);
        setDirection(1);
        setPreviousStep(currentStep);
        setCurrentStep(getNextIncompleteStep(currentStep));
      } catch (error: any) {
        setIsLoading(false);
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || 'Failed to save username',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error('Error saving username:', error);
      }
      return;
    }
    
    // Step 8 (Avatar) - Just move to next step (optional step)
    if (currentStep === 8) {
      // Always log touchpoint as complete so user won't be re-prompted
      // (This is a one-time prompt - once confirmed, never show again)
      logTouchpointMutation.mutate({touchpoint: 'profile_picture_update', action: 'complete'});
      
      // Mark step 8 (IntroAvatar) as complete
      markStepComplete(8);
      setDirection(1);
      setPreviousStep(currentStep);
      setCurrentStep(getNextIncompleteStep(currentStep));
      return;
    }

    // Step 9 (Places) - Just move to next step
    if (currentStep === 9) {
      if (sessionPlacesCount < 3) {
        setShowPlacesExitModal(true);
        return;
      }
      markStepComplete(9);
      setShowPlacesToEventsBridge(true);
      setDirection(1);
      setPreviousStep(currentStep);
      setCurrentStep(getNextIncompleteStep(currentStep));
      return;
    }

    // Step 10 (Bookmarks) - Just move to next step
    if (currentStep === 10) {
      if (sessionBookmarkCount < 3) {
        setShowBookmarkExitModal(true);
        return;
      }
      markStepComplete(10);
      setDirection(1);
      setPreviousStep(currentStep);
      setCurrentStep(getNextIncompleteStep(currentStep));
      return;
    }

    // Step 11 (Notifications) - Save notification preferences and complete
    if (currentStep === 11) {
      try {
        setIsLoading(true);
        
        // Update user with notification preferences
        const updateData: any = {
          sms_enabled: smsEnabled,
          email_enabled: emailEnabled,
        };

        // Handle push notifications
        if (pushEnabled) {
          if (window['ReactNativeWebView']) {
            // React Native app - request permission (token will be handled by event listener in pushService)
            pushService.pushPermissionRequest();
            // Don't set push_enabled here - the event listener will handle it when token is received
          } else {
            if (!isIphone) {
              // Non-iOS web - can get token directly
              try {
                const token = await pushService.askNotificationPermission();
                if (token) {
                  updateData.device_token = token;
                  updateData.device_platform = deviceDetected();
                  updateData.push_enabled = true;
                }
              } catch (error) {
                console.error('Failed to get push notification token:', error);
                // Don't set push_enabled without a token
              }
            } else {
              // iOS web - request permission (token will be handled by event listener in pushService)
              pushService.pushPermissionRequest();
              // Don't set push_enabled here - the event listener will handle it when token is received
            }
          }
        } else {
          updateData.push_enabled = false;
        }

        await updateUser(updateData);
        
        // Mark step 11 (IntroNotifications) as complete - this is the last tracked step
        markStepComplete(11);

        toast({
          title: fromBanner ? 'Profile updated!' : 'Welcome to the community!',
          description: fromBanner ? 'Thanks for updating your community info!' : "You're all set. Let's get started!",
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        setIsLoading(false);
        navigate('/');
      } catch (error: any) {
        setIsLoading(false);
        console.error('Error saving notification preferences:', error);
        // Don't block user from continuing even if notification save fails
        // Still mark as complete since they clicked Done
        markStepComplete(11);
        toast({
          title: fromBanner ? 'Profile updated!' : 'Welcome to the community!',
          description: fromBanner ? 'Thanks for updating your community info!' : "You're all set. Let's get started!",
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate('/');
      }
      return;
    }
    
    // All other steps - just move forward, skipping completed profile steps
    if (currentStep < TOTAL_STEPS - 1) {
      setDirection(1);
      setPreviousStep(currentStep);
      setCurrentStep(getNextIncompleteStep(currentStep));
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      await requestVerificationCode(phone);
      toast({
        title: 'Code resent',
        description: 'Check your phone for the new code',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast({
        title: 'Error',
        description: 'Failed to resend code',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setPreviousStep(currentStep);
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Map onboarding steps to their corresponding banner IDs.
   * Used to dismiss the banner for the current step when user taps "I'm all set".
   */
  const stepToBannerId: Record<number, string> = {
    6: 'confirm-home-address',
    7: 'confirm-screen-name',
    8: 'confirm-profile-photo',
    9: 'complete-onboarding',
    10: 'complete-onboarding',
  };

  /**
   * Handle "I'm all set" exit when user has completed all required fields.
   * Marks onboarding as complete and dismisses the banner for the current step.
   */
  const handleExit = () => {
    // Dismiss the banner that corresponds to the current onboarding step
    const bannerId = stepToBannerId[currentStep];
    if (bannerId) {
      addDismissedBanner(bannerId);
    }

    markOnboardingComplete();
    toast({
      title: fromBanner ? 'Profile updated!' : 'Welcome to the community!',
      description: fromBanner ? 'Thanks for updating your community info!' : "You're all set. Let's get started!",
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate('/');
  };

  const handleSoftCheckContinue = () => {
    setShowSoftCheckModal(false);
    setSoftCheckCompleted(true);
    // Keep pendingAddressSubmission true so the else-if in handleNext knows to skip soft check
    // Continue with address submission
    handleNext();
  };

  const handleSoftCheckCancel = () => {
    setShowSoftCheckModal(false);
    setPendingAddressSubmission(false);
    setSoftCheckCompleted(false);
    setIsLoading(false);
  };

  // Check if we're transitioning from intro3 to intro4 or vice versa
  const isFadeTransition =
    (previousStep === 2 && currentStep === 3) || (previousStep === 3 && currentStep === 2);

  const variants = {
    enter: (custom: {direction: number; fade: boolean}) => ({
      x: custom.fade ? 0 : custom.direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (custom: {direction: number; fade: boolean}) => ({
      x: custom.fade ? 0 : custom.direction > 0 ? '-100%' : '100%',
      opacity: 0,
    }),
  };

  const CurrentStepComponent = steps[currentStep].component;
  const buttonText = steps[currentStep].buttonText;
  // Hide back button if user came from banner and is on step 6 (address) or later
  // This prevents users from going back to earlier steps they shouldn't access
  const showBackButton = steps[currentStep].showBackButton && !(fromBanner && currentStep >= 6);
  const showProgress = steps[currentStep].showProgress;
  const requiresValidation = steps[currentStep].requiresValidation;
  const isContinueDisabled = requiresValidation && !isFormValid;

  // Diagnostic logging for button disabled state (CT-5853)
  React.useEffect(() => {
    if (requiresValidation) {
      const buttonState = {
        step: currentStep,
        stepName: steps[currentStep].buttonText,
        requiresValidation,
        isFormValid,
        isLoading,
        isContinueDisabled: isContinueDisabled || isLoading,
      };
      
      logger.debug('Onboarding button state', 'user_action', buttonState);
      
      // Track in analytics when button is disabled despite user being on validation step
      // This helps us measure frequency of the reported issue (CT-5853)
      if (isContinueDisabled && !isLoading) {
        logAnalyticsEvent('onboarding_button_disabled', {
          step: currentStep,
          step_name: steps[currentStep].buttonText,
          is_form_valid: isFormValid,
        });
      }
    }
  }, [currentStep, requiresValidation, isFormValid, isLoading, isContinueDisabled]);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="white"
      overflow="hidden"
      display="flex"
      flexDirection="column">
      {/* Header */}
      <Box px={4} py={4} bg={currentStep === 11 ? '#F8F6F7' : 'transparent'}>
        <HStack justify="space-between" align="center">
          <Box>
            <Image src={wordLogo} alt="Walkabout" h="32px" />
          </Box>
          <IconButton
            aria-label="Menu"
            icon={<Icon iconName="mdi mdi-dots-horizontal" />}
            variant="ghost"
            size="sm"
          />
        </HStack>
      </Box>

      {/* Progress Bar - Static position, fades in only when first appearing */}
      <AnimatePresence initial={false}>
        {showProgress && (
          <motion.div
            key="progress-bar"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2, delay: isFadeTransition ? 0.2 : 0}}>
            <Box px={4} pb={3}>
              <VStack align="stretch" spacing={1}>
                <Progress
                  value={progressPercentage}
                  size="xs"
                  bg={theme.colors.gray[200]}
                  borderRadius="full"
                  sx={{
                    '& > div[role="progressbar"]': {
                      transition: 'all 0.5s ease-in-out',
                      background:
                        'linear-gradient(90deg, rgba(201, 19, 107, 0.90) 0%, rgba(255, 122, 0, 0.90) 50%, rgba(122, 31, 255, 0.90) 100%)',
                    },
                  }}
                />
                <Text fontSize="12px" color={theme.colors.gray[600]}>
                  {steps[currentStep].progressText || ''}
                </Text>
              </VStack>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Area */}
      <Box flex={1} position="relative" overflow="hidden">
        <AnimatePresence
          initial={false}
          mode={isFadeTransition ? 'wait' : 'sync'}
          custom={{direction, fade: isFadeTransition}}>
          <motion.div
            key={currentStep}
            custom={{direction, fade: isFadeTransition}}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              duration: isFadeTransition ? 0.2 : 0.3,
              ease: 'easeInOut',
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
            {currentStep === 0 && <IntroWelcome />}
            {currentStep === 1 && <IntroFeatures />}
            {currentStep === 2 && <IntroCallToAction />}
            {currentStep === 3 && (
              <IntroPhone
                onValidationChange={setIsFormValid}
                onDataChange={(phoneNumber: string) => setPhone(phoneNumber)}
                initialPhone={phone}
              />
            )}
            {currentStep === 4 && (
              <IntroVerify
                onValidationChange={setIsFormValid}
                onDataChange={(code: string) => setVerificationCode(code)}
                phoneNumber={phone}
                onResendCode={handleResendCode}
              />
            )}
            {currentStep === 5 && (
              <IntroAccountInfo
                onValidationChange={setIsFormValid}
                onDataChange={(data: any) => setFormData(data)}
                initialData={formData}
              />
            )}
            {currentStep === 6 && (
              <IntroAddress
                onValidationChange={setIsFormValid}
                onDataChange={(addr: AddressData | null) => setAddress(addr)}
                initialAddress={address || undefined}
              />
            )}
            {currentStep === 7 && (
              <IntroUsername
                onValidationChange={setIsFormValid}
                onDataChange={(user: string) => setUsername(user)}
                initialUsername={username}
              />
            )}
            {currentStep === 8 && <IntroAvatar onValidationChange={setIsFormValid} />}
            {currentStep === 9 && (
              <IntroPlaces
                onSessionBookmarkCountChange={setSessionPlacesCount}
                showExitConfirmation={showPlacesExitModal}
                onExitConfirmationClose={() => setShowPlacesExitModal(false)}
                onExitConfirmationSkip={() => {
                  setShowPlacesExitModal(false);
                  markStepComplete(9);
                  setShowPlacesToEventsBridge(true);
                  setDirection(1);
                  setPreviousStep(currentStep);
                  setCurrentStep(currentStep + 1);
                }}
              />
            )}
            {currentStep === 10 && (
              <IntroBookmarks
                onSessionBookmarkCountChange={setSessionBookmarkCount}
                showExitConfirmation={showBookmarkExitModal}
                onExitConfirmationClose={() => setShowBookmarkExitModal(false)}
                onExitConfirmationSkip={() => {
                  setShowBookmarkExitModal(false);
                  markStepComplete(10);
                  setDirection(1);
                  setPreviousStep(currentStep);
                  setCurrentStep(currentStep + 1);
                }}
                suppressWelcomeModal={showPlacesToEventsBridge}
              />
            )}
            {currentStep === 11 && (
              <IntroNotifications
                onValidationChange={setIsFormValid}
                onEmailEnabledChange={setEmailEnabled}
                onSmsEnabledChange={setSmsEnabled}
                onPushEnabledChange={setPushEnabled}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Bottom Navigation */}
      <Box px={4} pb={8} pt={4} bg={currentStep === 11 ? '#F8F6F7' : 'transparent'}>
        <VStack spacing={4}>
          {/* Progress Dots - Only show on first 3 intro pages */}
          {currentStep < 3 && (
            <HStack spacing={2}>
              {Array.from({length: 3}).map((_, index) => (
                <Box
                  key={index}
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={index === currentStep ? theme.colors.pink[500] : '#E0E0E0'}
                  transition="all 0.3s"
                />
              ))}
            </HStack>
          )}

          {/* Back Button and Continue Button */}
          <HStack w="100%" spacing={4} align="center">
            {showBackButton && (
              <Box
                as="button"
                h="52px"
                w="52px"
                borderRadius="50%"
                bg="white"
                border={`1px solid ${theme.colors.gray[200]}`}
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={handleBack}
                position="relative"
                opacity={currentStep === 0 ? 0.3 : 1}
                cursor={currentStep === 0 ? 'not-allowed' : 'pointer'}
                _active={{transform: currentStep === 0 ? 'none' : 'scale(0.95)'}}>
                <Image src={angleLeft} alt="Arrow left" w="1rem" h="1rem" />
              </Box>
            )}

            <Box
              as="button"
              flex={1}
              bg={isContinueDisabled || isLoading ? theme.colors.gray[300] : theme.colors.pink[500]}
              color="white"
              py="12px"
              px="24px"
              borderRadius="full"
              onClick={handleNext}
              disabled={isContinueDisabled || isLoading}
              opacity={isContinueDisabled || isLoading ? 0.6 : 1}
              cursor={isContinueDisabled || isLoading ? 'not-allowed' : 'pointer'}
              _active={{
                transform: isContinueDisabled || isLoading ? 'none' : 'scale(0.98)',
              }}
              transition="all 0.2s">
              <TextG16 fontWeight="500" lineHeight="24px">
                {isLoading ? 'Loading...' : buttonText}
              </TextG16>
            </Box>
          </HStack>

          {/* Settings reminder text - only show on notifications step */}
          {currentStep === 11 && (
            <TextG14 textAlign="center" lineHeight="22px" color="#262626" fontWeight="500">
              You can change your notification preferences anytime in Settings.
            </TextG14>
          )}

          {/* "I'm all set" exit button - show on steps 6+ when all required fields are populated */}
          {currentStep >= 6 && currentStep < 11 && profileFields.allRequiredComplete && (
            <Box as="button" onClick={handleExit} cursor="pointer" _active={{opacity: 0.7}}>
              <TextG16 fontWeight="500" color="#636363" textDecoration="underline">
                I'm all set
              </TextG16>
            </Box>
          )}

          {/* Maybe later button - only show on notifications step */}
          {currentStep === 11 && (
            <Box as="button" onClick={handleNext} cursor="pointer" _active={{opacity: 0.7}}>
              <TextG16 fontWeight="500" color="#636363" textDecoration="underline">
                Maybe later
              </TextG16>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Soft Check Modal */}
      <SoftCheckModal
        isOpen={showSoftCheckModal}
        onClose={handleSoftCheckCancel}
        onContinue={handleSoftCheckContinue}
        onCancel={handleSoftCheckCancel}
      />

      {/* Places → Events bridge modal: introduces the second bookmark step. */}
      <Modal
        isOpen={showPlacesToEventsBridge && currentStep === 10}
        onClose={() => setShowPlacesToEventsBridge(false)}
        isCentered
        size="sm">
        <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
        <ModalContent mx={4} borderRadius="24px" py={8} px={6}>
          <ModalBody p={0}>
            <VStack spacing={6} align="stretch">
              <TextG24 fontSize="28px" fontWeight="700" textAlign="center" lineHeight="36px" color="#262626">
                Awesome!
              </TextG24>
              <VStack spacing={5} align="stretch">
                <Text textAlign="center" fontSize="20px" lineHeight="28px" color="#262626">
                  Now that you've seen{' '}
                  <Text as="span" color="#276ACD" fontWeight="700">
                    where to go local
                  </Text>
                </Text>
                <Text textAlign="center" fontSize="20px" lineHeight="28px" color="#262626">
                  <Text as="span" color="#C9136B" fontWeight="700">
                    next see what to do
                  </Text>{' '}
                  nearby including events, meet-ups and special perks!
                </Text>
              </VStack>
              <Box
                as="button"
                bg="#C9136B"
                color="white"
                py={4}
                borderRadius="full"
                boxShadow="inset 0 -4px 0 rgba(0, 0, 0, 0.25)"
                onClick={() => setShowPlacesToEventsBridge(false)}
                _active={{transform: 'scale(0.98)'}}
                transition="all 0.2s">
                <TextG16 fontWeight="600">Continue to See What to Do Local</TextG16>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Onboarding;
